import { getApiEnvironmentTable } from '~/components/ContentTools/apiSkillEnvironments';

const EXPERIENCE_API_ENDPOINTS = [
  {
    label: 'Curated offer',
    method: 'GET',
    path: '/api/experiences/offers/:experienceId?currency=AUD&brand=:brand&curationData=true&disableCache=true',
    notes: 'Current curated experience offer data shown in Admin. Use this before editing.',
  },
  {
    label: 'Update curated offer',
    method: 'PATCH',
    path: '/api/experiences/offers/:experienceId?currency=AUD&brand=:brand',
    notes: 'Applies a partial curation payload for editable experience fields.',
  },
  {
    label: 'Original provider offer',
    method: 'GET',
    path: '/api/experiences/offers/:experienceId/original?brand=:brand',
    notes: 'Provider-sourced original content for comparison on Rezdy experiences.',
  },
  {
    label: 'Curation feature manager',
    method: 'GET',
    path: '/api/experiences/offers/:experienceId/curation-feature-manager',
    notes: 'Feature flags that indicate which fields are visible or editable.',
  },
  {
    label: 'Categories',
    method: 'GET',
    path: '/api/experiences/categories?currency=AUD&brand=:brand',
    notes: 'Available categories used when editing the experience category list.',
  },
  {
    label: 'Available dates',
    method: 'GET',
    path: '/api/experiences/offers/:experienceId/availability?brand=:brand',
    notes: 'Availability dates for ticket and booking checks.',
  },
  {
    label: 'Available times',
    method: 'GET',
    path: '/api/experiences/offers/:experienceId/availability/:day?brand=:brand&curationData=true',
    notes: 'Availability times for a selected day.',
  },
];

const EXPERIENCE_PATCH_AREAS = [
  {
    area: 'Experience tab',
    fields: '`title`, `location`, `dealDescription`, `shortDescription`, `included`, `images`, `video`',
  },
  {
    area: 'Additional information',
    fields: '`thingsToKnowNew`, `cancellationInfo`, `vendorBookingCutoff`, `allowBuyNowBookLater`, `allowGiftCard`',
  },
  {
    area: 'Deal options',
    fields: '`tickets`, `bookingOptions`, `bookingFields`, `features`, `ticketUnitLabel`, `ticketed`',
  },
  {
    area: 'Pricing and discounts',
    fields:
      '`baseSalesPrice`, `margin`, `commissionMargin`, `bundleMinMarkupPct`, `memberDiscountEnabled`, `memberDiscountOverrides`',
  },
  {
    area: 'Categories and audiences',
    fields: '`categories`, `targetAudiences`',
  },
  {
    area: 'Curation options and schedule',
    fields:
      '`curationStatus`, `curationComment`, `isFlash`, `showPostPurchase`, `searchEngineMarketingEnabled`, `unlisted`, `showInJourneys`, `disableReviews`, `idSalesforceExternal`, `brands`, `brandSchedules`',
  },
];

export function getExperiencesCodexSkillText() {
  const environmentTable = getApiEnvironmentTable();
  const endpointTable = EXPERIENCE_API_ENDPOINTS.map(
    ({ label, method, path, notes }) => `| ${label} | \`${method}\` | \`${path}\` | ${notes} |`,
  ).join('\n');
  const patchAreasTable = EXPERIENCE_PATCH_AREAS.map(({ area, fields }) => `| ${area} | ${fields} |`).join('\n');

  return `# Luxury Escapes Admin Experiences API Editing Skill

Use this skill when a Luxury Escapes content user asks Codex to inspect or edit an experience through the Admin Portal API instead of navigating the browser UI.

## Inputs to ask the user for

- The API details block copied from the Admin Portal. It should include \`API_BASE_URL\` and \`ADMIN_BEARER_TOKEN\`.
- Which environment they want edited: \`test\` or \`prod\`.
- The experience ID, usually from an Admin URL like \`/experience/:experienceId\`.
- The brand to edit, usually the Admin tenant brand such as \`luxuryescapes\`.
- The exact content change requested.

Never ask the user to paste long-lived credentials. Never print the bearer token back to the user. Keep it only in memory for the current task.

## API hosts

Do not call the Admin Portal web-app host, such as \`https://test-admin.luxuryescapes.com\` or \`https://admin.luxuryescapes.com\`. The Admin Portal talks to a separate API host. Prefer the copied \`API_BASE_URL\` value from the Admin Portal. If the user did not paste API details, choose the API base URL from the environment:

| Environment | API base URL | Use for |
| --- | --- | --- |
${environmentTable}

If the user is working from a review app or a local setup with a custom backend, ask them to use the Admin Portal's "Copy API details" button so you get the exact configured \`window.configs.API_HOST\` value and a matching token.

## Authentication

Use the copied \`ADMIN_BEARER_TOKEN\` value. Send every API request with:

\`\`\`http
Authorization: Bearer <ADMIN_TOKEN>
Content-Type: application/json;charset=UTF-8
\`\`\`

## Endpoints

The base URL for requests is:

\`\`\`text
<API_BASE_URL><URL path above>
\`\`\`

Replace \`:experienceId\`, \`:brand\`, and \`:day\` with the values for the task.

| Area | Method | URL path | Use for |
| --- | --- | --- | --- |
${endpointTable}

If a request unexpectedly returns the Admin HTML shell instead of JSON, the host is wrong; switch from the Admin Portal web-app host to the API base URL for that environment.

## Editable PATCH fields

The update endpoint accepts a partial curation payload. Fetch the current curated offer first, then send only the fields needed for the requested change unless preserving nested arrays or objects requires including the full edited value for that field.

| Admin area | Common PATCH fields |
| --- | --- |
${patchAreasTable}

## Workflow

1. GET the curated offer with \`curationData=true\` and inspect the returned \`result\`.
2. If field visibility or editability is uncertain, GET the curation feature manager before editing.
3. Locate the smallest PATCH field or nested object that contains the requested content.
4. Make a minimal JSON edit. Preserve unrelated IDs, ordering, booleans, arrays, money objects, ticket modes, booking fields, and provider data.
5. Show the user a concise before/after summary before PATCHing, unless they already asked you to apply directly.
6. PATCH the partial payload to \`/api/experiences/offers/:experienceId?currency=AUD&brand=:brand\`.
7. GET the curated offer again with \`curationData=true\` and verify the expected content is present.
8. If the user asks to test a change and revert it, capture the original value first, PATCH the test value, verify, then PATCH the original value back and verify again.

## Safety rules

- Prefer GET before PATCH. Do not construct payloads from memory.
- Do not modify images, prices, tickets, availability, booking fields, schedules, or provider IDs unless the user specifically asks.
- When editing arrays such as \`tickets\`, \`images\`, \`categories\`, \`targetAudiences\`, or \`brandSchedules\`, preserve all existing entries and only change the targeted item.
- Do not copy fields from the original provider offer over curated content unless the user explicitly asks.
- If the schema, feature manager, or current data does not clearly identify the target field, stop and ask for the exact field or tab instead of guessing.
- If a PATCH fails, report the status and response body without retrying destructive changes blindly.
`;
}
